import React, { useState } from 'react';
import { useOps } from '../context/OpsContext';
import { Car, Search, Plus, Filter, MoreVertical, Edit2, Trash2, MapPin, Settings, Calendar, X, Shield, Tool } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import './VehicleManager.css';

const VehicleManager = () => {
    const { vehicles = [], loading, addVehicle, updateVehicle, deleteVehicle, branches = [] } = useOps();
    const [searchTerm, setSearchTerm] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingVehicle, setEditingVehicle] = useState(null);
    const [formData, setFormData] = useState({
        make: '',
        model: '',
        registration_number: '',
        branch: '',
        status: 'Available'
    });

    const filteredVehicles = (vehicles || []).filter(v =>
        (v.registration_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (v.make || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (v.model || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (editingVehicle) {
            await updateVehicle(editingVehicle.id, formData);
        } else {
            await addVehicle(formData);
        }
        setIsModalOpen(false);
        setEditingVehicle(null);
        setFormData({ make: '', model: '', registration_number: '', branch: '', status: 'Available' });
    };

    const handleEdit = (v) => {
        setEditingVehicle(v);
        setFormData({
            make: v.make,
            model: v.model,
            registration_number: v.registration_number,
            branch: v.branch,
            status: v.status
        });
        setIsModalOpen(true);
    };

    if (loading) return <LoadingSpinner color="purple-600" message="Inspecting Fleet..." />;

    return (
        <div className="page-container">
            <div className="page-header">
                <div>
                    <h2 className="page-title">Vehicle Fleet</h2>
                    <p className="page-subtitle">Monitor maintenance and assignments for company assets</p>
                </div>
                <button
                    onClick={() => setIsModalOpen(true)}
                    className="btn btn-primary flex items-center gap-2"
                    style={{ backgroundColor: '#9333ea' }}
                >
                    <Plus size={20} /> Add New Vehicle
                </button>
            </div>

            <div className="filters-bar">
                <div className="search-input-wrapper">
                    <Search size={18} className="search-icon" />
                    <input
                        type="text"
                        placeholder="Search by registration, make or model..."
                        className="search-input"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <button className="btn btn-secondary flex items-center gap-2">
                    <Filter size={16} /> Filter
                </button>
            </div>

            <div className="table-container card fade-in">
                <table className="data-table">
                    <thead>
                        <tr>
                            <th>Asset Info</th>
                            <th>Registration</th>
                            <th>Branch Assignment</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredVehicles.length > 0 ? filteredVehicles.map((v) => (
                            <tr key={v.id}>
                                <td>
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center border border-purple-100">
                                            <Car size={20} />
                                        </div>
                                        <div>
                                            <div className="text-sm font-bold text-slate-800 uppercase tracking-tight">{v.make}</div>
                                            <div className="text-[10px] text-slate-400 font-bold uppercase">{v.model}</div>
                                        </div>
                                    </div>
                                </td>
                                <td>
                                    <span className="font-mono text-xs bg-slate-900 text-white px-2.5 py-1 rounded-lg tracking-widest uppercase">
                                        {v.registration_number}
                                    </span>
                                </td>
                                <td>
                                    <div className="flex items-center gap-2">
                                        <MapPin size={14} className="text-slate-400" />
                                        <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
                                            {v.branch || 'Floating Asset'}
                                        </span>
                                    </div>
                                </td>
                                <td>
                                    <span className={`status-badge ${(v.status || '').toLowerCase().replace(' ', '-')}`}>
                                        {v.status}
                                    </span>
                                </td>
                                <td>
                                    <div className="flex gap-1">
                                        <button onClick={() => handleEdit(v)} className="icon-btn" title="Edit">
                                            <Edit2 size={16} />
                                        </button>
                                        <button onClick={() => deleteVehicle(v.id)} className="icon-btn text-red" title="Delete">
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        )) : (
                            <tr>
                                <td colSpan="5" className="text-center py-20 text-secondary">
                                    <div className="flex flex-col items-center gap-3 opacity-30">
                                        <Car size={40} />
                                        <p className="font-bold uppercase tracking-widest text-xs">No assets found matching criteria</p>
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {isModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content card max-w-md fade-in">
                        <div className="modal-header border-b pb-4 mb-6">
                            <h3 className="text-xl font-black text-slate-800 tracking-tight">
                                {editingVehicle ? 'Update Vehicle Record' : 'Register New Asset'}
                            </h3>
                            <button onClick={() => { setIsModalOpen(false); setEditingVehicle(null); }} className="p-2 hover:bg-slate-50 rounded-full transition-colors text-slate-400">
                                <X size={20} />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} className="premium-form">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="form-group">
                                    <label>MAKE</label>
                                    <input
                                        required
                                        type="text"
                                        placeholder="e.g. Toyota"
                                        value={formData.make}
                                        onChange={e => setFormData({ ...formData, make: e.target.value })}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>MODEL</label>
                                    <input
                                        required
                                        type="text"
                                        placeholder="e.g. Corolla"
                                        value={formData.model}
                                        onChange={e => setFormData({ ...formData, model: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="form-group">
                                <label>REGISTRATION NUMBER</label>
                                <input
                                    required
                                    type="text"
                                    placeholder="Plate Number"
                                    style={{ letterSpacing: '0.1em', fontWeight: 'bold' }}
                                    value={formData.registration_number}
                                    onChange={e => setFormData({ ...formData, registration_number: e.target.value })}
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="form-group">
                                    <label>DEPLOYMENT BRANCH</label>
                                    <select
                                        value={formData.branch}
                                        onChange={e => setFormData({ ...formData, branch: e.target.value })}
                                    >
                                        <option value="">Unassigned</option>
                                        {branches.map(b => (
                                            <option key={b.id} value={b.branch_name || b.name}>{b.branch_name || b.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>ASSET STATUS</label>
                                    <select
                                        value={formData.status}
                                        onChange={e => setFormData({ ...formData, status: e.target.value })}
                                    >
                                        <option value="Available">Available</option>
                                        <option value="In Use">In Use</option>
                                        <option value="Maintenance">Maintenance</option>
                                        <option value="Repair">Repair</option>
                                    </select>
                                </div>
                            </div>
                            <div className="flex gap-3 pt-6">
                                <button type="button" className="btn btn-secondary flex-1" onClick={() => setIsModalOpen(false)}>Discard</button>
                                <button type="submit" className="btn btn-primary flex-1">Save Asset</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default VehicleManager;
